import { configureAmplify, TenantNotFoundError } from './amplify-config';
import { fetchRumConfig } from './lib/rum-config';
import { initRum, recordBootstrapEvent, recordError } from './lib/rum';

/**
 * Application bootstrap (#3019). Extracted from the `main.tsx` IIFE so the
 * telemetry wiring is unit-testable without driving `ReactDOM.createRoot`.
 *
 * Ordering is load-bearing: RUM is fetched and initialised at the very top,
 * BEFORE `configureAmplify`, so the client is up in time to capture a failure
 * of the tenant-configuration fetch (the #3016 white-screen case). All RUM
 * calls are best-effort and fully guarded — telemetry never changes user-facing
 * behaviour or the bootstrap contract.
 *
 * @returns `true` if Amplify configured successfully (the caller should render
 *   the React app), `false` if bootstrap failed (the pre-React error UI was
 *   shown and React must not mount).
 */
export async function bootstrap(): Promise<boolean> {
    // 1. Bring up telemetry first, before anything that can fail. fetchRumConfig
    //    already swallows its own errors, but guard here too so a RUM problem
    //    can never block bootstrap.
    let rumConfig: Awaited<ReturnType<typeof fetchRumConfig>>;
    try {
        rumConfig = await fetchRumConfig();
    } catch {
        rumConfig = null;
    }
    initRum(rumConfig);
    installGlobalErrorHandlers();

    try {
        await configureAmplify({
            onRetry: (attempt, maxAttempts) => {
                const t = document.getElementById('pre-react-loader-text');
                if (t) {
                    t.textContent = 'Reconnecting…';
                }
                recordBootstrapEvent('bootstrap_retry', { attempt, maxAttempts });
            },
        });
        return true;
    } catch (err: unknown) {
        const root = document.getElementById('root');
        if (root) {
            while (root.firstChild) {
                root.removeChild(root.firstChild);
            }
        }

        if (err instanceof TenantNotFoundError) {
            recordBootstrapEvent('bootstrap_failed', { reason: 'tenant_not_found' });
            const tenantNotFoundContainer = document.getElementById('pre-react-tenant-not-found');
            if (tenantNotFoundContainer) {
                tenantNotFoundContainer.style.display = 'flex';
            }
            return false;
        }

        recordError(err, { source: 'bootstrap' });
        recordBootstrapEvent('bootstrap_failed', { reason: 'config_error' });

        const errorContainer = document.getElementById('pre-react-error');
        const errorMessage = document.getElementById('pre-react-error-message');
        if (errorContainer) {
            errorContainer.style.display = 'flex';
        }
        if (errorMessage) {
            errorMessage.textContent = err instanceof Error ? err.message : 'An unexpected error occurred';
        }
        return false;
    }
}

/**
 * Installs window-level `error` and `unhandledrejection` handlers that forward
 * to RUM. Both are best-effort — `recordError` is fully guarded. Installed
 * after `initRum` so events captured during Amplify configuration are reported.
 */
function installGlobalErrorHandlers(): void {
    window.addEventListener('error', (event: ErrorEvent) => {
        recordError(event.error ?? event.message, { source: 'window.onerror' });
    });
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        recordError(event.reason, { source: 'unhandledrejection' });
    });
}
